/**
 * assets/product-data-fetcher.js
 *
 * Shared JS helper for the on-demand product-data endpoint.
 * Project-wide rule: see .claude/docs/data-fetching-pattern.md (sec 3 — "The JS helper").
 *
 * Public API (attaches to `theme.productDataFetcher`):
 *   fetch(family, handle, ...extras)       → Promise<TypedShape | { error: string, ... }>
 *   prefetch(family, handle, ...extras)    → Promise (fire-and-forget warm-up)
 *   fetchOptionState(productUrl, sectionId, optionValueIds)
 *                                          → Promise<selection-aware product data>
 *   invalidate(family, handle, ...extras)  → void  (drop a single cache entry)
 *   invalidateOptionState(productUrl, sectionId, optionValueIds)
 *                                          → void  (drop one option-state entry,
 *                                             so a pre-add stock re-read is live)
 *   clear()                                → void  (wipe every cache)
 *
 * Behavior:
 *   - URL: /search?view=product-data&q=<encoded>&type=product
 *   - q built as `family--handle--extras` per data-fetching-pattern.md sec 2.1
 *   - In-memory cache keyed by exact q string, lifetime = page-load
 *   - Single-flight: concurrent fetch() calls for the same q share the same Promise
 *   - text.trim() before JSON.parse (Liquid leaks whitespace)
 *   - Failed responses (network, HTTP error, parse failure) are also cached;
 *     call invalidate() to retry. This keeps behavior predictable.
 *   - Loading UX is the section's responsibility, not the helper's.
 */
(function () {
  'use strict';

  window.theme = window.theme || {};

  var ENDPOINT_BASE = '/search?view=product-data&type=product';
  var cache = new Map(); // q-string → Promise<typed payload | error envelope>
  var optionStateCache = new Map(); // section-rendering URL → Promise<typed payload | error envelope>

  var BUYBOX_POPOVER_SELECTOR = [
    '[data-size-popover]',
    '[data-selector-popover]',
    '[data-color-popover]',
    '[data-color-popup]',
    '[data-pack-popup]'
  ].join(',');

  var HEADER_MENU_SELECTOR = [
    '#siteNav .lvl1.parent.megamenu',
    '#siteNav .lvl1.parent.dropdown',
    '[data-nav-a-mega]',
    '#navA-mobile',
    '.js-mobile-nav-toggle'
  ].join(',');

  function closeBuyboxPopoversForHeaderMenu(event) {
    if (!event.target || typeof event.target.closest !== 'function') return;
    if (!event.target.closest(HEADER_MENU_SELECTOR)) return;

    Array.prototype.forEach.call(document.querySelectorAll(BUYBOX_POPOVER_SELECTOR), function (popover) {
      if (typeof popover.hidePopover !== 'function' || typeof popover.matches !== 'function') return;
      try {
        if (popover.matches(':popover-open')) popover.hidePopover();
      } catch (error) {}
    });
  }

  if (!theme.buyboxHeaderLayeringBound) {
    theme.buyboxHeaderLayeringBound = true;
    document.addEventListener('pointerdown', closeBuyboxPopoversForHeaderMenu, true);
    document.addEventListener('mouseover', closeBuyboxPopoversForHeaderMenu);
    document.addEventListener('focusin', closeBuyboxPopoversForHeaderMenu);
  }

  function buildQ(family, handle, extras) {
    var parts = [String(family), String(handle)];
    if (extras && extras.length) {
      for (var i = 0; i < extras.length; i++) {
        parts.push(String(extras[i]));
      }
    }
    return parts.join('--');
  }

  function buildUrl(q) {
    return ENDPOINT_BASE + '&q=' + encodeURIComponent(q);
  }

  function doFetch(q) {
    return fetch(buildUrl(q))
      .then(function (response) {
        if (!response.ok) {
          return { error: 'http_' + response.status, status: response.status };
        }
        return response.text().then(function (text) {
          try {
            return JSON.parse(String(text).trim());
          } catch (e) {
            return { error: 'parse_failed', message: e && e.message ? e.message : String(e) };
          }
        });
      })
      .catch(function (err) {
        return { error: 'network_failed', message: err && err.message ? err.message : String(err) };
      });
  }

  function fetchOnce(q) {
    if (cache.has(q)) {
      return cache.get(q);
    }
    var promise = doFetch(q);
    cache.set(q, promise);
    return promise;
  }

  function buildOptionStateUrl(productUrl, sectionId, optionValueIds) {
    var separator = String(productUrl).indexOf('?') === -1 ? '?' : '&';
    var ids = (optionValueIds || []).filter(Boolean).join(',');
    return String(productUrl)
      + separator
      + 'section_id=' + encodeURIComponent(sectionId)
      + (ids ? '&option_values=' + encodeURIComponent(ids) : '');
  }

  function fetchOptionStateOnce(url) {
    if (optionStateCache.has(url)) {
      return optionStateCache.get(url);
    }

    var promise = fetch(url)
      .then(function (response) {
        if (!response.ok) {
          return { error: 'http_' + response.status, status: response.status };
        }
        return response.text().then(function (html) {
          try {
            var documentRoot = new DOMParser().parseFromString(String(html), 'text/html');
            var payloadElement = documentRoot.querySelector('[data-byo-product-option-state]');
            if (!payloadElement) {
              return { error: 'missing_option_state' };
            }
            return JSON.parse(String(payloadElement.textContent).trim());
          } catch (e) {
            return { error: 'parse_failed', message: e && e.message ? e.message : String(e) };
          }
        });
      })
      .catch(function (err) {
        return { error: 'network_failed', message: err && err.message ? err.message : String(err) };
      });

    optionStateCache.set(url, promise);
    return promise;
  }

  theme.productDataFetcher = {
    fetch: function (family, handle) {
      var extras = Array.prototype.slice.call(arguments, 2);
      return fetchOnce(buildQ(family, handle, extras));
    },

    prefetch: function (family, handle) {
      var extras = Array.prototype.slice.call(arguments, 2);
      var p = fetchOnce(buildQ(family, handle, extras));
      p.catch(function () {});
      return p;
    },

    fetchOptionState: function (productUrl, sectionId, optionValueIds) {
      return fetchOptionStateOnce(buildOptionStateUrl(productUrl, sectionId, optionValueIds));
    },

    invalidate: function (family, handle) {
      var extras = Array.prototype.slice.call(arguments, 2);
      cache.delete(buildQ(family, handle, extras));
    },

    invalidateOptionState: function (productUrl, sectionId, optionValueIds) {
      optionStateCache.delete(buildOptionStateUrl(productUrl, sectionId, optionValueIds));
    },

    clear: function () {
      cache.clear();
      optionStateCache.clear();
    }
  };
})();
